import styled from "@emotion/styled";
import {waitForTx} from "@waves/waves-transactions";
import {observer} from "mobx-react-lite";
import {Link} from "react-router-dom";

import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';

import {useEffect, useState} from "react";
import {getAddressCollections, getDaoVotes, IVote} from "./Data";
import {useStores} from "src/stores/useStores";
import CardStore from "src/stores/CardStore";

import Button from "./Button";
import VoteRatio from "./VoteRatio";
import Tabs from "../Tabs";
import SizedBox from "../SizedBox";

import {NODE_URL_MAP} from "src/constants";

import Skeleton from "react-loading-skeleton";

const MAINNET_ADDRESS = NODE_URL_MAP.W;

interface ICellProps {
    first?: boolean;
    last?: boolean;
}

interface IImageDiv {
    src: string;
}

interface ICollectionsProps {
    stakedNftsList?: string[];
}

enum TABS {
    INACTIVE='Inactive',
    ACTIVE='Active',
    CONFIRMED='Confirmed',
    REJECTED='Rejected'
}

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    border: 1px solid #F1F2FE;
    border-radius: 16px;
    overflow: hidden;
    min-width: 480px;
`;

const GridRow = styled.div`
    display: contents;
`;

const ColumnHead = styled.div<ICellProps>`
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #8082C5;
    border-bottom: 1px solid #F1F2FE;
    padding: ${({first, last}) => `18px ${last ? '24px' : '14px'} 18px ${first ? '24px' : '14px'}`};
`;

const CollectionName = styled.div`
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #363870;
    min-width: 120px;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const CollectionDesc = styled.div`
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #8082C5;
    min-width: 120px;
`;

const Cell = styled.div<ICellProps>`
    padding: ${({first, last}) => `18px ${last ? '24px' : '14px'} 18px ${first ? '24px' : '14px'}`};
    border-bottom: 1px solid #F1F2FE;
`;

const Image = styled.div<IImageDiv>`
    width: 40px;
    height: 40px;
    background: #D9D9D9;
    border-radius: 12px;
    margin-right: 8px;
    background-image: ${({src}) => `url('${src}')`};
    background-size: cover;
    background-position: center;
    cursor: pointer;
    display: inline-flex;
`;

const MoreImage = styled.div`
    width: 40px;
    height: 40px;
    background: #F1F2FE;
    border-radius: 12px;
    margin-right: 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: #7075E9;
`;

const StyledLink = styled(Link)`
    color: #363870;
`;

const ImageWrap = styled.div`
    display: flex;
`;

const ActionRow = styled.div`
    
    @media only screen and (min-width : 768px) {
        display: flex;
    }
`;

const TotalInfo = styled.div`
    font-weight: 400;
    color: #8082C5;
    font-size: 12px;
    text-align: center;
    line-height: 18px;
    display: flex;
    justify-content: space-between;
`;

const YesPower = styled.div`
    color: #7075E9;
    min-width: 20px;
`;

const NoPower = styled.div`
    color: #EE505A;
    min-width: 20px;
`;

export default observer(function(props: ICollectionsProps) {
    const {walletStore, accountStore, notificationStore} = useStores();
    const [activeTab, setActiveTab] = useState<TABS>(TABS.ACTIVE);

    const [daoVotes, setDaoVotes] = useState<{[x: string]: IVote}>();
    const [unactiveCollections, setUnactiveCollections] = useState<{[x:string]: CardStore[]}>();
    const [collections, setCollections] = useState<{[x:string]: CardStore[]}>();

    const [galleryIndexOpen, setGalleryIndexOpen] = useState(-1);
    const [imageIndex, setImageIndex] = useState(0);

    const [update, setUpdate] = useState(0);

    const tabs = [{
        id: TABS.INACTIVE,
        title: 'Inactive',
        count: unactiveCollections ? Object.keys(unactiveCollections).length : 0,
        active: activeTab === TABS.INACTIVE,
        onClick: (id: string) => {setActiveTab(id as TABS)}
    }, {
        id: TABS.ACTIVE,
        title: 'Active',
        count: daoVotes ? Object.keys(daoVotes || {}).filter((id) => !daoVotes[id].confirmed && !daoVotes[id].rejected && daoVotes[id].createdAt).length : 0,
        active: activeTab === TABS.ACTIVE,
        onClick: (id: string) => {setActiveTab(id as TABS)}
    }, {
        id: TABS.CONFIRMED,
        title: 'Confirmed',
        count: daoVotes ? Object.keys(daoVotes || {}).filter((id) => daoVotes[id].confirmed && daoVotes[id].createdAt).length : 0,
        active: activeTab === TABS.CONFIRMED,
        onClick: (id: string) => {setActiveTab(id as TABS)}
    }, {
        id: TABS.REJECTED,
        title: 'Rejected',
        count: daoVotes ? Object.keys(daoVotes || {}).filter((id) => daoVotes[id].rejected && daoVotes[id].createdAt).length : 0,
        active: activeTab === TABS.REJECTED,
        onClick: (id: string) => {setActiveTab(id as TABS)}
    }];

    useEffect(() => {
        Promise.all([
            getDaoVotes(),
            getAddressCollections(accountStore.address || '')
        ]).then(([votes, addressCollections]) => {
            const collectionIds: string[] = [];
            Object.keys(votes).forEach((voteId) => {
                if (votes[voteId].collectionId) {
                    collectionIds.push(votes[voteId].collectionId);
                }
            });

            const unactiveUserCollections = addressCollections.filter(x => !collectionIds.includes(x));
            if (collectionIds.length) {
                walletStore.loadCollections(collectionIds).then((result) => {
                    setDaoVotes(votes);
                    setCollections(result);
                });
            }

            if (addressCollections.length) {
                walletStore.loadCollections(unactiveUserCollections).then((result) => {
                    setUnactiveCollections(result);
                });
                if (!collectionIds.length) {
                    setActiveTab(TABS.INACTIVE);
                }
            }
        });
    }, [update, accountStore.address]);

    useEffect(() => {
        const interval = setInterval(() => {
            setUpdate(update + 1);
        }, 30000);
        return () => {
            clearInterval(interval);
        };
    }, [update]);

    const vote = async (voteId: string, fn: string, type?: 'up'|'down') => {
        if (!voteId) return;
        if (!props.stakedNftsList?.length) {
            notificationStore.notify('Not avalible staked NFTs', {
                type: 'error',
                title: 'Error'
            });
            return;
        }
        try {
            const txId = await accountStore.invoke({
                dApp: '3P975LUuqc7uBPiSxp3nemf2FZh62zfirSh',
                payment: [],
                call: {
                    function: fn,
                    args: [
                        {type: 'string', value: voteId},
                        {type: 'string', value: type ? props.stakedNftsList.map(item => item + "_" + type).join('|') : ""}
                    ]
                }
            });
            await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
            if (txId) {
                setUpdate(update + 1);
                notificationStore.notify(`Completed!`, {
                    type: "success",
                    title: fn === 'voteByNFT' ? 'Your vote has been accepted' : 'Your vote has been canceled',
                    link: `https://wavesexplorer.com/tx/${txId}`,
                    linkTitle: "View on Explorer",
                });
            }
        } catch (e: any) {
            console.error(e);
            notificationStore.notify(e.message ?? JSON.stringify(e), {
                type: 'error',
                title: 'Transaction is not completed'
            });
        }
    };

    const collectionVote = async (collectionId: string, fn: string) => {
        if (!collectionId) return;
        try {
            const txId = await accountStore.invoke({
                dApp: '3P975LUuqc7uBPiSxp3nemf2FZh62zfirSh',
                payment: [],
                call: {
                    function: fn,
                    args: [
                        {type: 'string', value: collectionId}
                    ]
                }
            });
            await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
            if (txId) {
                setUpdate(update + 1);
                notificationStore.notify(`Completed!`, {
                    type: "success",
                    title: fn === 'sendToVote' ? 'Your collection has been voted on' : 'Your collection has been voted down',
                    link: `https://wavesexplorer.com/tx/${txId}`,
                    linkTitle: "View on Explorer",
                });
            }
        } catch (e: any) {
            console.error(e);
            notificationStore.notify(e.message ?? JSON.stringify(e), {
                type: 'error',
                title: 'Transaction is not completed'
            });
        }
    };
    
    return (
        <div>
            <Tabs align="left" tabs={tabs}/>
            <SizedBox height={24}/>
            <Grid>
                <GridRow>
                    <ColumnHead first={true}>Name</ColumnHead>
                    <ColumnHead>Details</ColumnHead>
                    <ColumnHead>{activeTab !== TABS.INACTIVE ? 'Yes/No ratio' : ''}</ColumnHead>
                    <ColumnHead last={true}>Actions</ColumnHead>
                </GridRow>
                {
                    activeTab === TABS.INACTIVE && unactiveCollections ?
                        Object.keys(unactiveCollections).map((collection, galleryIndex) => {
                            const first = unactiveCollections[collection][0];
                            let imagesSet: Set<string> = new Set<string>();
                            unactiveCollections[collection].forEach((item) => {
                                if (item.sourceList) {
                                    item.sourceList.split(',').forEach(sitem => imagesSet.add(sitem));
                                } else {
                                    imagesSet.add(item.imageUrl);
                                }
                            });

                            const images = Array.from(imagesSet);
                            return <GridRow key={collection}>
                                <Cell first={true}>
                                    <CollectionName>
                                        <StyledLink to={first.collectionUrl || ''}>{first.collectionName}</StyledLink>
                                    </CollectionName>
                                    <CollectionDesc>{first.description}</CollectionDesc>
                                </Cell>
                                <Cell>
                                    {galleryIndexOpen === galleryIndex ? <Lightbox
                                        mainSrc={images[imageIndex]}
                                        nextSrc={images.length > 1 ? images[(imageIndex + 1) % images.length] : undefined}
                                        prevSrc={images.length > 1 ? images[(imageIndex + images.length - 1) % images.length] : undefined}
                                        onCloseRequest={() => {setGalleryIndexOpen(-1)}}
                                        onMovePrevRequest={() => {
                                            setImageIndex((imageIndex + images.length - 1) % images.length);
                                        }}
                                        onMoveNextRequest={() => {
                                            setImageIndex((imageIndex + 1) % images.length);
                                        }}
                                    /> : ''}
                                    <ImageWrap>
                                        {
                                            images.slice(0, 4).map((src, index) => {
                                                return <Image
                                                key={index}
                                                src={src}
                                                onClick={() => {
                                                    setGalleryIndexOpen(galleryIndex);
                                                    setImageIndex(index);
                                                }}/>
                                            })
                                        }
                                        {
                                            images.length > 4 ? <MoreImage onClick={() => {
                                                setGalleryIndexOpen(galleryIndex);
                                                setImageIndex(4);
                                            }}>{images.length - 4}+</MoreImage> : ''
                                        }
                                    </ImageWrap>
                                </Cell>
                                <Cell></Cell>
                                <Cell last={true}>
                                    <ActionRow>
                                        <Button caption={`To voting`} size="small" width={84} viewMode='primary' onClick={() => { collectionVote(collection, 'sendToVote') }}/>
                                    </ActionRow>
                                </Cell>
                            </GridRow>
                        }) : ''
                }
                {
                    activeTab !== TABS.INACTIVE ?
                    collections && daoVotes ? Object.keys(daoVotes).filter((voteId) => {
                        if (activeTab === TABS.CONFIRMED) return daoVotes[voteId].confirmed;
                        else if (activeTab === TABS.REJECTED) return daoVotes[voteId].rejected;
                        return !daoVotes[voteId].confirmed && !daoVotes[voteId].rejected;
                    }).map((voteId, galleryIndex) => {
                        let imagesSet: Set<string> = new Set<string>();
                        if (!daoVotes[voteId].collectionId || !collections[daoVotes[voteId].collectionId]) return;
                        const first = collections[daoVotes[voteId].collectionId][0];
                        collections?.[daoVotes[voteId].collectionId].forEach((item) => {
                            if (item.sourceList) {
                                item.sourceList.split(',').forEach(sitem =>  imagesSet.add(sitem))
                            } else {
                                imagesSet.add(item.imageUrl)
                            }
                        });

                        const images = Array.from(imagesSet).reverse();
                        const yesPercent = daoVotes[voteId].totalPower > 0 ? ((daoVotes[voteId].upPower || 0) / daoVotes[voteId].totalPower) * 100 : 0;
                        const noPercent = daoVotes[voteId].totalPower > 0 ? ((daoVotes[voteId].downPower || 0)/ daoVotes[voteId].totalPower) * 100 : 0;
                        const addressVotes: {[x:string]: {power: number; type: string; up:number; down:number;}} = {};
                        Object.keys(daoVotes).forEach((voteId) => {
                            Object.keys(daoVotes[voteId].voters).forEach((address) => {
                                if (address === accountStore.address) {
                                    addressVotes[voteId] = daoVotes[voteId].voters[address];
                                }
                            })
                            
                        })
                        return <GridRow key={voteId}>
                                <Cell first={true}>
                                    <CollectionName>
                                        <StyledLink to={first.collectionUrl || ''}>{first.collectionName}</StyledLink>&nbsp;
                                        {
                                            accountStore.address && daoVotes[voteId].collectionId.indexOf(accountStore.address || '') === 0 &&
                                            !daoVotes[voteId].confirmed && !daoVotes[voteId].rejected ?
                                                <Button caption={`Remove from voting`} size="small" width={145} viewMode='secondary' onClick={() => { collectionVote(voteId, 'removeFromVote') }}/> : ''
                                        }
                                    </CollectionName>
                                    <CollectionDesc>{first.description}</CollectionDesc>
                                </Cell>
                                <Cell>
                                    {galleryIndexOpen === galleryIndex ? <Lightbox
                                        mainSrc={images[imageIndex]}
                                        nextSrc={images.length > 1 ? images[(imageIndex + 1) % images.length] : undefined}
                                        prevSrc={images.length > 1 ? images[(imageIndex + images.length - 1) % images.length] : undefined}
                                        onCloseRequest={() => {setGalleryIndexOpen(-1)}}
                                        onMovePrevRequest={() => {
                                            setImageIndex((imageIndex + images.length - 1) % images.length);
                                        }}
                                        onMoveNextRequest={() => {
                                            setImageIndex((imageIndex + 1) % images.length);
                                        }}
                                    /> : ''}
                                    <ImageWrap>
                                        {
                                            images.slice(0, 4).map((src, index) => {
                                                return <Image
                                                key={index}
                                                src={src}
                                                onClick={() => {
                                                    setGalleryIndexOpen(galleryIndex);
                                                    setImageIndex(index);
                                                }}/>
                                            })
                                        }
                                        {
                                            images.length > 4 ? <MoreImage onClick={() => {
                                                setGalleryIndexOpen(galleryIndex);
                                                setImageIndex(4);
                                            }}>{images.length - 4}+</MoreImage> : ''
                                        }
                                    </ImageWrap>
                                </Cell>
                                <Cell>
                                    <VoteRatio data={{allow: yesPercent, disallow: noPercent}}/>
                                    {
                                        daoVotes[voteId].totalPower ?
                                            <TotalInfo>
                                                <YesPower>
                                                    { addressVotes?.[voteId]?.up || '' }
                                                </YesPower>
                                                <div>
                                                    { daoVotes[voteId].totalPower || 0 } / { daoVotes[voteId].quorum || 0 }
                                                </div>
                                                <NoPower>
                                                    { addressVotes?.[voteId]?.down || '' }
                                                </NoPower>
                                            </TotalInfo> : ''
                                    }
                                </Cell>
                                <Cell last={true}>
                                    {
                                        !daoVotes[voteId].confirmed && !daoVotes[voteId].rejected ?
                                            (!addressVotes[voteId] ?
                                            <ActionRow>
                                                <Button size="small" caption="Yes" width={64} onClick={() => {vote(voteId, 'voteByNFT', 'up')}} />
                                                <SizedBox width={8} height={8}/>
                                                <Button size="small" viewMode="danger" width={64} caption="No" onClick={() => {vote(voteId, 'voteByNFT', 'down')}}/>
                                            </ActionRow> :
                                            <ActionRow>
                                                <Button caption={`Cancel`} size="small" width={64} viewMode='secondary' onClick={() => {vote(voteId, 'unvoteByNFT')}}/>
                                            </ActionRow>)
                                            : ''
                                    }
                                </Cell>
                            </GridRow>
                    }) : <GridRow>
                            <Cell first={true}>
                                <Skeleton width={140} height={20} />
                                <Skeleton width={40} height={20} />
                            </Cell>
                            <Cell>
                                <ImageWrap>
                                    <Skeleton width={40} height={40} />
                                    <SizedBox width={8} />
                                    <Skeleton width={40} height={40} />
                                    <SizedBox width={8} />
                                    <Skeleton width={40} height={40} />
                                </ImageWrap>
                            </Cell>
                            <Cell>
                                <Skeleton width={200} height={8} />
                            </Cell>
                            <Cell>
                                <ActionRow>
                                    <Skeleton width={64} height={32} />
                                    <SizedBox width={8} />
                                    <Skeleton width={64} height={32} />
                                </ActionRow>
                            </Cell>
                        </GridRow>
                    : ''
                }
            </Grid>
        </div>
    );
});